import type { BatchUserPayload, PendingCustomerUser } from "shared";

export const makePendingId = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

/**
 * site_id / customer_user_type are omitted when unset rather than sent as empty
 * strings — the backend contract (MAU-1) falls back to the customer's default
 * site and the Standard category on an absent key. `customer_user_type` carries
 * the category's `mapping_id`.
 */
export const buildBatchPayload = (
  users: PendingCustomerUser[],
): BatchUserPayload[] =>
  users.map((user) => ({
    full_name: user.name.trim(),
    email: user.email.trim(),
    IsPrivileged: user.isPrivileged,
    ...(user.siteValue ? { site_id: user.siteValue } : {}),
    ...(user.categoryValue ? { customer_user_type: user.categoryValue } : {}),
  }));

export const normalizeEmailKey = (email: string): string =>
  email.trim().toLowerCase();

/**
 * Single source of truth for the "already queued" rule, used by BOTH the add
 * form and the inline row editor. Pass the rows the candidate must not collide
 * with (the editor excludes the row being edited). Matching is case-insensitive
 * because `applyCustomerUserBatchResultAtom` reconciles the server response by
 * lower-cased email — two rows sharing an address make that reconciliation
 * ambiguous and can silently drop a failure.
 */
export const isDuplicatePendingEmail = (
  email: string,
  rows: PendingCustomerUser[],
): boolean => {
  const candidate = normalizeEmailKey(email);
  return rows.some((row) => normalizeEmailKey(row.email) === candidate);
};

export const DUPLICATE_EMAIL_MESSAGE = "This email is already in the list";
